import { useMemo } from 'react'
import { env } from '../../common/util'
import { useAccount } from '../../common/useAccount'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { RoleGroup, RoleType } from '../../models/Role'

interface EnvRoleMapping {
  prod: string
  staging: string
  dev: string
}

export enum RoleGroupKind {
  INVITABLE,
  CROSS_ACCOUNT_GRANTABLE,
}

interface RoleGroupWithKind extends RoleGroup {
  kinds: RoleGroupKind[]
}

export const ownerRoles: EnvRoleMapping = { prod: 'c7jximvkjagbh0n8', staging: '9zip1cx4oi5lt3ii', dev: '5raikjdb92ws5ju6' }
export const unpaidOwnerRoles: EnvRoleMapping = { prod: 'd30ac23565e311ef', staging: 'byj32b0qejhtrd9z', dev: 'esmqgjn3tld2e5jf' }
export const adminRoles: EnvRoleMapping = { prod: '1cuebwhxuxhzdvvi', staging: '2fj5t1nz70wd4f4l', dev: 'dh90p1kwu2s84wes' }
export const enterpriseAdminRoles: EnvRoleMapping = { prod: 'hodbdwvxxtpzjff4', staging: 'mknezlto36cms69t', dev: 'jrwef3zugrfarzsf' }
export const professionalRoles: EnvRoleMapping = { prod: '98rhc42q33x2vqjl', staging: '1304esp8q7uxgc9n', dev: '10cyp13r3pvudt93' }
export const sidecarRoles: EnvRoleMapping = { prod: 'nb5aphuvqngljca3', staging: 'dsqeuufsbuxejirp', dev: 'fsjjasjrvihxtpuk' }
export const templateAdminRoles: EnvRoleMapping = { prod: 'chpnkcxeljl6cu2d', staging: '2bo3smest8vgw5z3', dev: '33m56jk846xgfe1r' }
// custom
export const schedulingAdminRoles: EnvRoleMapping = { prod: '11bq2aua0hkilwtb', staging: '11bq2aua0hkilwtb', dev: '11bq2aua0hkilwtb' }
export const templatesAndMeetingTypesAdminRoles: EnvRoleMapping = { prod: '8g6oa75j9cxm96ye', staging: '8g6oa75j9cxm96ye', dev: '8g6oa75j9cxm96ye' }
export const rfgParentAccounts: EnvRoleMapping = { prod: 'en444aid8zxlms5c', staging: '3svhxag33nfen9fu', dev: '8xher7c1gj99jalu' }
const RFG_ROLE_GROUP_NAMES = ['RFG Advisor', 'RFG Support Staff']
const SCHEDULING_ROLE_GROUP_NAMES = ['Administrative Assistant with Scheduling', 'Administrative Assistant with Templates, Scheduling']
const SCHEDULING_ENABLED_QUOTA = 'scheduling.enabled.value'

export const roleGroups: RoleGroupWithKind[] = [
  {
    name: 'User',
    roleIds: [professionalRoles[env]],
    type: RoleType.SEAT,
    kinds: [RoleGroupKind.INVITABLE],
  },
  {
    name: 'Template Manager User',
    roleIds: [professionalRoles[env], templateAdminRoles[env]],
    type: RoleType.SEAT,
    kinds: [RoleGroupKind.INVITABLE],
  },
  {
    name: 'Administrative Assistant',
    roleIds: [sidecarRoles[env]],
    type: RoleType.SIDECAR,
    kinds: [RoleGroupKind.INVITABLE, RoleGroupKind.CROSS_ACCOUNT_GRANTABLE],
  },
  {
    name: 'Administrative Assistant with Templates',
    roleIds: [sidecarRoles[env], templateAdminRoles[env]],
    type: RoleType.SIDECAR,
    kinds: [RoleGroupKind.INVITABLE, RoleGroupKind.CROSS_ACCOUNT_GRANTABLE],
  },
  {
    name: 'Administrator',
    roleIds: [adminRoles[env], professionalRoles[env]],
    type: RoleType.SEAT,
    kinds: [RoleGroupKind.INVITABLE],
  },
  {
    name: 'Owner',
    roleIds: [ownerRoles[env], professionalRoles[env]],
    type: RoleType.SEAT,
    kinds: [RoleGroupKind.INVITABLE],
  },
  {
    name: 'Owner, Enterprise Administrator',
    roleIds: [ownerRoles[env], enterpriseAdminRoles[env], professionalRoles[env]],
    type: RoleType.SEAT,
    kinds: [],
  },
  {
    name: 'Administrator, Enterprise Administrator',
    roleIds: [adminRoles[env], enterpriseAdminRoles[env], professionalRoles[env]],
    type: RoleType.SEAT,
    kinds: [],
  },
  {
    name: 'RFG Advisor',
    roleIds: [professionalRoles[env], schedulingAdminRoles[env], templatesAndMeetingTypesAdminRoles[env]],
    type: RoleType.SEAT,
    kinds: [RoleGroupKind.INVITABLE],
  },
  {
    name: 'RFG Support Staff',
    roleIds: [sidecarRoles[env], templateAdminRoles[env], schedulingAdminRoles[env], templatesAndMeetingTypesAdminRoles[env]],
    type: RoleType.SIDECAR,
    kinds: [RoleGroupKind.INVITABLE],
  },
  {
    name: 'Administrative Assistant with Scheduling',
    roleIds: [sidecarRoles[env], schedulingAdminRoles[env]],
    type: RoleType.SIDECAR,
    kinds: [RoleGroupKind.INVITABLE, RoleGroupKind.CROSS_ACCOUNT_GRANTABLE],
  },
  {
    name: 'Administrative Assistant with Templates, Scheduling',
    roleIds: [sidecarRoles[env], schedulingAdminRoles[env], templateAdminRoles[env]],
    type: RoleType.SIDECAR,
    kinds: [RoleGroupKind.INVITABLE, RoleGroupKind.CROSS_ACCOUNT_GRANTABLE],
  },
]

// True for the RFG parent account itself and its child accounts
const isRfgAccount = (accountId?: string, parentAccountId?: string) =>
  !!rfgParentAccounts[env] && (accountId === rfgParentAccounts[env] || parentAccountId === rfgParentAccounts[env])

interface RoleGroupFilter {
  onlyInvitable?: boolean
  onlyCrossAccountGrantable?: boolean
}

interface InviteRoleGroupFilter extends RoleGroupFilter {
  excludeRfg?: boolean
}

const filterRoleGroups = ({ onlyInvitable = false, onlyCrossAccountGrantable = false }: RoleGroupFilter, includeRfg: boolean, includeScheduling = true) =>
  roleGroups.filter(
    (rg) =>
      (!onlyInvitable || rg.kinds.includes(RoleGroupKind.INVITABLE)) &&
      (!onlyCrossAccountGrantable || rg.kinds.includes(RoleGroupKind.CROSS_ACCOUNT_GRANTABLE)) &&
      (includeRfg || !RFG_ROLE_GROUP_NAMES.includes(rg.name)) &&
      (includeScheduling || !SCHEDULING_ROLE_GROUP_NAMES.includes(rg.name)),
  ) as RoleGroup[]

// The standard role groups, never the RFG ones: use useInviteRoleGroups for the groups the current account may assign
export const getInviteRoleGroups = (filter: RoleGroupFilter = {}) => filterRoleGroups(filter, false)

const useIsRfgAccount = () => {
  const { accountId, account } = useAccount()
  return isRfgAccount(accountId, account?.parentId)
}

// The role groups the current account may assign: the RFG groups only show up in the RFG account tree
export const useInviteRoleGroups = ({ onlyInvitable = false, onlyCrossAccountGrantable = false, excludeRfg = false }: InviteRoleGroupFilter = {}) => {
  const includeRfg = useIsRfgAccount() && !excludeRfg
  const { hasQuota } = useZocksQuota()
  const includeScheduling = hasQuota(SCHEDULING_ENABLED_QUOTA)
  return useMemo(
    () => filterRoleGroups({ onlyInvitable, onlyCrossAccountGrantable }, includeRfg, includeScheduling),
    [onlyInvitable, onlyCrossAccountGrantable, includeRfg, includeScheduling],
  )
}

export const getRoleGroupForRoles = (roleIds: string[]): RoleGroup | null => {
  const inviteRole = roleGroups.find(
    (roleGroup) => roleGroup.roleIds.length === roleIds.length && [...roleGroup.roleIds].sort().every((val, index) => val === [...roleIds].sort()[index]),
  )
  return inviteRole || null
}
